<script setup lang="ts">
import { Loader2, Pencil } from 'lucide-vue-next'
import { ESection, EImage } from '@/components/base/Email'
import EmailBlockWrapper from './EmailBlockWrapper.vue'
import EmailImageToolbar from './EmailImageToolbar.vue'
import { useImageCropUpload } from '@/components/ui/ImageUploader/useImageCropUpload'
import type { ImageBlock } from '../../types'

import type { ImageMaskShape } from '../../types'

const props = defineProps<{
  block: ImageBlock
  isSelected: boolean
  isUploading?: boolean
  isMaskBusy?: boolean
  colorPresets?: { color: string; label: string }[]
}>()

const emit = defineEmits<{
  (e: 'select'): void
  (e: 'upload-file', file: File): void
  (e: 'update:block', block: ImageBlock): void
  (e: 'mask-change', mask: ImageMaskShape): void
}>()

const { t } = useI18n()
const fileInputRef = ref<HTMLInputElement | null>(null)
const isImageLoading = ref(!!props.block.src)

const { cropSrc, handleFileSelected, onCropConfirm, onCropCancel } = useImageCropUpload((file) =>
  emit('upload-file', file)
)

watch(
  () => props.block.src,
  (newSrc, oldSrc) => {
    if (newSrc && newSrc !== oldSrc) isImageLoading.value = true
  }
)

const onImageLoaded = () => {
  isImageLoading.value = false
}

const onImageError = () => {
  isImageLoading.value = false
}

const showOverlayLoader = computed(() => props.isUploading || isImageLoading.value)

const triggerUpload = () => {
  fileInputRef.value?.click()
}
</script>

<template>
  <EmailBlockWrapper
    :is-selected="isSelected"
    :label="t('pages.email.emailTemplateCreatePage.blockImage')"
    cursor="pointer"
    :block-background-color="block.blockBackgroundColor"
    @select="emit('select')"
  >
    <EmailImageToolbar
      v-if="isSelected && block.src"
      :block="block"
      :is-mask-busy="isMaskBusy"
      :color-presets="colorPresets"
      @update:block="emit('update:block', $event)"
      @mask-change="emit('mask-change', $event)"
      @replace="triggerUpload"
    />

    <input
      ref="fileInputRef"
      type="file"
      accept="image/jpeg, image/png, image/gif, image/webp"
      class="hidden"
      @change="handleFileSelected"
    />
    <ESection
      :style="{
        padding: '10px 0',
        textAlign: block.align
      }"
      @click="!block.src && triggerUpload()"
    >
      <div v-if="block.src" class="group/image relative inline-block" :style="{ width: `${block.width}%` }">
        <EImage
          :src="block.src"
          :alt="block.alt"
          :style="{
            width: '100%',
            height: 'auto',
            borderRadius: '8px'
          }"
          @load="onImageLoaded"
          @error="onImageError"
        />
        <div
          v-if="!showOverlayLoader"
          class="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all group-hover/image:bg-black/20 group-hover/image:opacity-100"
        >
          <button
            type="button"
            class="pointer-events-auto inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-foreground shadow ring-1 ring-border hover:bg-muted/50"
            @click.stop="triggerUpload"
          >
            <Pencil class="size-4" />
            {{ t('pages.email.emailTemplateCreatePage.changeImage') }}
          </button>
        </div>
        <div v-if="showOverlayLoader" class="absolute inset-0 flex items-center justify-center rounded-lg bg-white/70">
          <Loader2 class="size-6 animate-spin text-primary" />
        </div>
      </div>
      <div
        v-else-if="isUploading"
        class="flex h-64 w-full items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted"
      >
        <Loader2 class="size-6 animate-spin text-primary" />
      </div>
      <div
        v-else
        class="flex h-64 w-full cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted transition-colors hover:border-border hover:bg-accent"
      >
        <div class="text-center">
          <BaseIcon name="HeroPhotoIconSolid" class="mx-auto h-10 w-10 text-foreground-disabled" />
          <p class="mt-2 text-sm text-muted-foreground">
            {{ t('pages.email.emailTemplateCreatePage.clickToAddImage') }}
          </p>
          <p class="text-xs text-muted-foreground">
            {{ t('pages.email.emailTemplateCreatePage.imageFormatInfo') }}
          </p>
        </div>
      </div>
    </ESection>

    <ImageCropDialog :src="cropSrc" @confirm="onCropConfirm" @cancel="onCropCancel" />
  </EmailBlockWrapper>
</template>
